/**
 * places-api-client.ts
 *
 * Google Places API (New) client for frontend React components
 * Replaces react-google-autocomplete with direct API calls to new Places API
 */

import { useState, useCallback } from 'react'

export interface PlacePrediction {
  place_id: string
  description: string
  main_text: string
  secondary_text: string
  types: string[]
}

export interface PlacesAutoCompleteResult {
  predictions: PlacePrediction[]
  status: 'OK' | 'ZERO_RESULTS' | 'INVALID_REQUEST' | 'ERROR'
}

interface NewPlacesAPIResponse {
  suggestions: Array<{
    placePrediction?: {
      place: string
      placeId: string
      text: {
        text: string
        matches: Array<{
          startOffset?: number
          endOffset: number
        }>
      }
      structuredFormat: {
        mainText: {
          text: string
          matches: Array<{
            startOffset?: number
            endOffset: number
          }>
        }
        secondaryText: {
          text: string
        }
      }
      types: string[]
    }
  }>
}

function convertToLegacyFormat(
  response: NewPlacesAPIResponse,
): PlacesAutoCompleteResult {
  if (!response.suggestions || response.suggestions.length === 0) {
    return {
      predictions: [],
      status: 'ZERO_RESULTS',
    }
  }

  const predictions: PlacePrediction[] = response.suggestions
    .filter((suggestion) => suggestion.placePrediction)
    .map((suggestion) => {
      const place = suggestion.placePrediction!
      return {
        place_id: place.placeId,
        description: place.text.text,
        main_text: place.structuredFormat.mainText.text,
        secondary_text: place.structuredFormat.secondaryText.text,
        types: place.types,
      }
    })

  return {
    predictions,
    status: 'OK',
  }
}

export async function getPlacePredictions(
  input: string,
  options: {
    types?: string[]
    componentRestrictions?: {
      country?: string
    }
    locationBias?: {
      center: {
        lat: number
        lng: number
      }
      radius: number
    }
  } = {},
): Promise<PlacesAutoCompleteResult> {
  try {
    if (!input || input.trim().length === 0) {
      return {
        predictions: [],
        status: 'ZERO_RESULTS',
      }
    }

    const requestBody: any = {
      input: input.trim(),
    }

    if (options.componentRestrictions?.country) {
      requestBody.includedRegionCodes = [
        options.componentRestrictions.country.toUpperCase(),
      ]
    }

    if (options.types && options.types.length > 0) {
      requestBody.includedPrimaryTypes = options.types
    }

    if (options.locationBias) {
      requestBody.locationBias = {
        circle: {
          center: {
            latitude: options.locationBias.center.lat,
            longitude: options.locationBias.center.lng,
          },
          radius: options.locationBias.radius,
        },
      }
    }

    const response = await fetch(
      'https://places.googleapis.com/v1/places:autocomplete',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        },
        body: JSON.stringify(requestBody),
      },
    )

    if (!response.ok) {
      console.error('Places API error:', response.status, response.statusText)
      return {
        predictions: [],
        status: 'ERROR',
      }
    }

    const data: NewPlacesAPIResponse = await response.json()
    return convertToLegacyFormat(data)
  } catch (error) {
    console.error('Places API request failed:', error)
    return {
      predictions: [],
      status: 'ERROR',
    }
  }
}

/**
 * React hook replacement for usePlacesService from react-google-autocomplete.
 * Merges initializer options (types, componentRestrictions) as defaults
 * into every getPlacePredictions call.
 */
export function usePlacesService(config: {
  apiKey?: string
  options?: {
    input?: string
    types?: string[]
    componentRestrictions?: {
      country?: string
    }
  }
}) {
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>(
    [],
  )
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] =
    useState(false)

  const defaultTypes = config.options?.types
  const defaultComponentRestrictions = config.options?.componentRestrictions

  const getPlacePredictionsWrapper = useCallback(
    async (requestOptions: {
      input: string
      types?: string[]
      componentRestrictions?: {
        country?: string
      }
      locationBias?: {
        center: {
          lat: number
          lng: number
        }
        radius: number
      }
    }) => {
      setIsPlacePredictionsLoading(true)

      try {
        const result = await getPlacePredictions(requestOptions.input, {
          types: requestOptions.types ?? defaultTypes,
          componentRestrictions:
            requestOptions.componentRestrictions ?? defaultComponentRestrictions,
          locationBias: requestOptions.locationBias,
        })

        setPlacePredictions(result.predictions)
      } catch (error) {
        console.error('Places prediction error:', error)
        setPlacePredictions([])
      } finally {
        setIsPlacePredictionsLoading(false)
      }
    },
    [defaultTypes, defaultComponentRestrictions],
  )

  return {
    placePredictions,
    getPlacePredictions: getPlacePredictionsWrapper,
    isPlacePredictionsLoading,
  }
}
